import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import { AlertCircle, CheckCircle2, Loader2, MailCheck, RotateCcw, ShieldCheck } from "lucide-react";
import { toast } from "react-toastify";
import { GLOBAL_IMAGES } from "../assets/asset";
import {
  extractStudentEmail,
  extractStudentJwtToken,
  extractStudentPreAuthToken,
  extractStudentUserRecord,
  isAlreadyVerifiedResponse,
  isStudentVerified,
  studentAuthApi,
} from "../features/auth/authApi";
import {
  STUDENT_OTP_ATTEMPTS,
  clearStudentVerificationFlow,
  createStudentVerificationFlow,
  decrementStudentOtpAttempts,
  formatStudentOtpTimer,
  getStoredStudentVerificationFlow,
  getStudentOtpRemainingMs,
  hasStudentOtpExpired,
  markStudentOtpExpired,
  persistStudentVerificationFlow,
} from "../features/auth/studentOtpFlow";
import { maskStudentEmail } from "../features/auth/studentSession";
import { finalizeStudentLogin } from "../features/auth/useLogin";

const GENERIC_RESEND_SUCCESS = "A new verification code has been sent.";
const GENERIC_VERIFY_SUCCESS = "Account verified successfully.";
const GENERIC_INVALID_OTP = "The verification code is invalid.";
const GENERIC_EXPIRED_OTP = "This verification code has expired.";
const GENERIC_ATTEMPTS_EXHAUSTED =
  "Too many failed attempts. Please request a new code.";
const GENERIC_PENDING_VERIFICATION = "Please verify your email to continue.";
const GENERIC_ALREADY_VERIFIED = "This account is already verified.";
const GENERIC_FAILURE = "Something went wrong. Please try again.";

const isExpiredOtpResponse = (payload) => {
  const message = String(payload?.message || "").toLowerCase();
  return message.includes("expire");
};

const isInvalidOtpResponse = (payload) => {
  const message = String(payload?.message || "").toLowerCase();
  return message.includes("invalid") || message.includes("incorrect");
};

const VerificationPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const storedFlow = useMemo(() => getStoredStudentVerificationFlow(), []);
  const [otp, setOtp] = useState("");
  const [flowState, setFlowState] = useState(() =>
    createStudentVerificationFlow(
      {
        ...storedFlow,
        email: String(location.state?.email || storedFlow.email || "")
          .trim()
          .toLowerCase(),
        source: String(location.state?.source || storedFlow.source || "signup").trim(),
      },
      { resetCycle: false }
    )
  );
  const [generalMessage, setGeneralMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState(
    String(location.state?.successMessage || "")
  );
  const [remainingMs, setRemainingMs] = useState(() =>
    getStudentOtpRemainingMs(flowState)
  );
  const lastStatusCheckEmailRef = useRef("");

  useEffect(() => {
    const currentFlow = getStoredStudentVerificationFlow();
    const nextFlow = persistStudentVerificationFlow({
      ...currentFlow,
      email: String(location.state?.email || currentFlow.email || "").trim().toLowerCase(),
      source: String(location.state?.source || currentFlow.source || "signup").trim(),
    });

    setFlowState(nextFlow);
  }, [location.state]);

  useEffect(() => {
    const timerId = window.setInterval(() => {
      const latestFlow = getStoredStudentVerificationFlow();
      const nextRemainingMs = getStudentOtpRemainingMs(latestFlow);

      if (nextRemainingMs === 0 && !hasStudentOtpExpired(latestFlow)) {
        markStudentOtpExpired();
      }

      setFlowState(latestFlow);
      setRemainingMs(nextRemainingMs);
    }, 1000);

    return () => window.clearInterval(timerId);
  }, []);

  const statusCheckMutation = useMutation({
    mutationFn: studentAuthApi.checkVerificationStatus,
    onSuccess: (response) => {
      const payload = response?.data || {};
      const responseData = payload?.data || {};

      if (isStudentVerified(responseData?.isVerified)) {
        clearStudentVerificationFlow();
        setGeneralMessage(GENERIC_ALREADY_VERIFIED);
        toast.info(GENERIC_ALREADY_VERIFIED, {
          ariaLabel: "Verification Info",
          autoClose: 2500,
          theme: "colored",
          hideProgressBar: true,
          className: "font-mali",
        });
        navigate("/login", { replace: true });
      } else {
        setGeneralMessage(GENERIC_PENDING_VERIFICATION);
      }
    },
    onError: (error) => {
      const responseData = error?.response?.data || {};
      const message = String(responseData?.message || "").toLowerCase();
      const statusCode = String(responseData?.status_code || "").trim();

      if (statusCode === "201" || message.includes("not verified")) {
        setGeneralMessage(GENERIC_PENDING_VERIFICATION);
        return;
      }

      setGeneralMessage("");
    },
  });

  useEffect(() => {
    const normalizedEmail = String(flowState.email || "").trim().toLowerCase();
    const normalizedSource = String(flowState.source || "").trim().toLowerCase();

    if (!normalizedEmail) {
      lastStatusCheckEmailRef.current = "";
      return;
    }

    if (normalizedSource && normalizedSource !== "signup") {
      lastStatusCheckEmailRef.current = "";
      return;
    }

    if (flowState.preAuthToken) {
      lastStatusCheckEmailRef.current = "";
      return;
    }

    if (lastStatusCheckEmailRef.current === normalizedEmail) return;

    lastStatusCheckEmailRef.current = normalizedEmail;
    statusCheckMutation.mutate({
      email: normalizedEmail,
    });
  }, [flowState.email, flowState.preAuthToken, flowState.source, statusCheckMutation]);

  const verifyOtpMutation = useMutation({
    mutationFn: studentAuthApi.verifySignupOtp,
    onSuccess: (response) => {
      const studentJwt = extractStudentJwtToken(response);
      const profileData = extractStudentUserRecord(response, {
        email: flowState.email,
        isVerified: "1",
        jwtoken: studentJwt,
      });

      if (!studentJwt) {
        setGeneralMessage(GENERIC_FAILURE);
        return;
      }

      clearStudentVerificationFlow();
      setGeneralMessage("");
      setSuccessMessage(GENERIC_VERIFY_SUCCESS);
      toast.success(GENERIC_VERIFY_SUCCESS, {
        ariaLabel: "Verification Success",
        autoClose: 2500,
        theme: "colored",
        hideProgressBar: true,
        className: "font-mali",
      });

      finalizeStudentLogin({
        dispatch,
        navigate,
        profileData,
        shouldShowFirstSignupDemo:
          String(flowState.source || "").trim().toLowerCase() === "signup",
      });
    },
    onError: (error) => {
      const responseData = error?.response?.data || {};

      if (isAlreadyVerifiedResponse(responseData)) {
        clearStudentVerificationFlow();
        setGeneralMessage(GENERIC_ALREADY_VERIFIED);
        navigate("/login", { replace: true });
        return;
      }

      if (isExpiredOtpResponse(responseData)) {
        const expiredFlow = markStudentOtpExpired();
        setFlowState(expiredFlow);
        setRemainingMs(0);
        setGeneralMessage(GENERIC_EXPIRED_OTP);
        return;
      }

      const nextFlow = decrementStudentOtpAttempts();
      setFlowState(nextFlow);
      setGeneralMessage(
        nextFlow.attemptsRemaining === 0
          ? GENERIC_ATTEMPTS_EXHAUSTED
          : isInvalidOtpResponse(responseData)
          ? GENERIC_INVALID_OTP
          : GENERIC_FAILURE
      );
    },
  });

  const resendOtpMutation = useMutation({
    mutationFn: ({ email }) => studentAuthApi.resendSignupOtp({ email }),
    onSuccess: (response) => {
      const responsePayload = response?.data || {};

      if (isAlreadyVerifiedResponse(responsePayload)) {
        clearStudentVerificationFlow();
        setGeneralMessage(GENERIC_ALREADY_VERIFIED);
        toast.info(GENERIC_ALREADY_VERIFIED, {
          ariaLabel: "Verification Info",
          autoClose: 2500,
          theme: "colored",
          hideProgressBar: true,
          className: "font-mali",
        });
        navigate("/login", { replace: true });
        return;
      }

      const nextFlow = createStudentVerificationFlow({
        ...flowState,
        email: extractStudentEmail(response) || flowState.email,
        preAuthToken: extractStudentPreAuthToken(response),
        verificationType: responsePayload?.data?.verification_type || "otp",
        accountType: responsePayload?.data?.account_type || "student",
      });

      setFlowState(nextFlow);
      setRemainingMs(getStudentOtpRemainingMs(nextFlow));
      setOtp("");
      setGeneralMessage("");
      setSuccessMessage(GENERIC_RESEND_SUCCESS);
      toast.success(GENERIC_RESEND_SUCCESS, {
        ariaLabel: "OTP Sent",
        autoClose: 2500,
        theme: "colored",
        hideProgressBar: true,
        className: "font-mali",
      });
    },
    onError: (error) => {
      const responseData = error?.response?.data || {};

      if (isAlreadyVerifiedResponse(responseData)) {
        clearStudentVerificationFlow();
        setGeneralMessage(GENERIC_ALREADY_VERIFIED);
        navigate("/login", { replace: true });
        return;
      }

      setGeneralMessage(GENERIC_FAILURE);
    },
  });

  const isExpired = hasStudentOtpExpired(flowState);
  const attemptsRemaining = flowState.attemptsRemaining;
  const canVerify =
    Boolean(flowState.preAuthToken) &&
    attemptsRemaining > 0 &&
    !isExpired &&
    otp.length === 6 &&
    !verifyOtpMutation.isPending;
  const canResend =
    Boolean(flowState.email) &&
    !resendOtpMutation.isPending &&
    flowState.canResendVerification !== false;
  const maskedEmail = maskStudentEmail(flowState.email);
  const helperText =
    attemptsRemaining === 0
      ? GENERIC_ATTEMPTS_EXHAUSTED
      : isExpired
      ? GENERIC_EXPIRED_OTP
      : "Enter the 6-digit code sent to your email.";

  const handleVerify = (event) => {
    event.preventDefault();

    if (!flowState.email) {
      setGeneralMessage(GENERIC_PENDING_VERIFICATION);
      return;
    }

    if (!flowState.preAuthToken) {
      setGeneralMessage("Request a new verification code to continue.");
      return;
    }

    if (!/^\d{6}$/.test(otp)) {
      setGeneralMessage(GENERIC_INVALID_OTP);
      return;
    }

    if (attemptsRemaining === 0) {
      setGeneralMessage(GENERIC_ATTEMPTS_EXHAUSTED);
      return;
    }

    if (isExpired) {
      markStudentOtpExpired();
      setGeneralMessage(GENERIC_EXPIRED_OTP);
      return;
    }

    setGeneralMessage("");
    setSuccessMessage("");
    verifyOtpMutation.mutate({
      pre_auth_token: flowState.preAuthToken,
      otp,
    });
  };

  const handleResend = () => {
    if (!canResend || !flowState.email) return;

    setGeneralMessage("");
    setSuccessMessage("");
    resendOtpMutation.mutate({
      email: flowState.email,
    });
  };

  return (
    <div
      className="verification-page min-h-screen flex items-center justify-center bg-gray-50 font-mali"
      style={{
        backgroundImage: `url(${GLOBAL_IMAGES.onboarding_bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="verification-page-card bg-white bg-opacity-70 rounded-[28px] shadow-lg px-6 py-6 md:px-10 md:py-7 w-full max-w-xl">
        <div className="verification-page-header flex items-center gap-3 mb-4">
          <img
            src={GLOBAL_IMAGES.LoginBackButton}
            alt="Back"
            className="verification-page-back-img w-12 h-12 cursor-pointer"
            onClick={() => navigate(-1)}
          />
          <div>
            <h2 className="verification-page-title text-3xl font-bold text-gray-800">Verify Your Email</h2>
            <p className="verification-page-subtitle text-gray-600 mt-1">Complete signup to continue.</p>
          </div>
        </div>

        <div className="verification-page-icon-row flex justify-center">
          <div className="verification-page-icon flex h-20 w-20 items-center justify-center rounded-full bg-[#eef5ff] text-[#08059B] shadow-sm">
            <MailCheck size={36} />
          </div>
        </div>

        <div className="verification-page-intro mt-4 text-center">
          <p className="verification-page-email text-lg font-semibold text-gray-800">
            {maskedEmail ? `Code sent to ${maskedEmail}` : "Verification code required"}
          </p>
          <p className="verification-page-helper text-sm text-gray-600 mt-1.5">{helperText}</p>
        </div>

        <div className="verification-page-stats mt-5 grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="verification-page-stat rounded-2xl border border-[#d9e2ff] bg-[#f7f9ff] px-4 py-3 text-center">
            <p className="verification-page-stat-label text-sm font-semibold uppercase tracking-wide text-[#4b5d95]">
              Time Remaining
            </p>
            <p className="verification-page-stat-value mt-1.5 text-2xl font-bold text-[#08059B]">
              {formatStudentOtpTimer(remainingMs)}
            </p>
          </div>
          <div className="verification-page-stat rounded-2xl border border-[#ffe2d2] bg-[#fff8f3] px-4 py-3 text-center">
            <p className="verification-page-stat-label text-sm font-semibold uppercase tracking-wide text-[#b15b2a]">
              Attempts Remaining
            </p>
            <p className="verification-page-stat-value mt-1.5 text-2xl font-bold text-[#d46c2d]">
              {attemptsRemaining}
            </p>
          </div>
        </div>

        <form onSubmit={handleVerify} className="verification-page-form mt-6 space-y-4">
          <div className="verification-page-field">
            <label
              htmlFor="student-otp"
              className="verification-page-label block text-lg font-bold text-gray-700 mb-2"
            >
              Verification Code
            </label>
            <div className="verification-page-input-wrap relative">
              <input
                id="student-otp"
                type="text"
                value={otp}
                onChange={(event) => {
                  const nextValue = event.target.value.replace(/\D/g, "").slice(0, 6);
                  setOtp(nextValue);
                  if (generalMessage) setGeneralMessage("");
                }}
                className="verification-page-input w-full rounded-full border-2 border-gray-300 px-5 py-3.5 pr-12 text-center text-2xl tracking-[0.35em] focus:outline-none focus:border-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100"
                placeholder="123456"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                disabled={!flowState.preAuthToken || attemptsRemaining === 0 || isExpired}
              />
              <ShieldCheck
                className="verification-page-input-icon absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"
                size={24}
              />
            </div>
          </div>

          {generalMessage ? (
            <div className="verification-page-message verification-page-error flex items-center gap-2 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
              <AlertCircle size={18} />
              <span>{generalMessage}</span>
            </div>
          ) : null}

          {successMessage ? (
            <div className="verification-page-message verification-page-success flex items-center gap-2 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm font-medium text-green-700">
              <CheckCircle2 size={18} />
              <span>{successMessage}</span>
            </div>
          ) : null}

          {!flowState.email ? (
            <div className="verification-page-message rounded-2xl border border-[#e8dcc9] bg-[#fffaf2] px-4 py-4 text-sm text-[#6b7280]">
              There is no active verification session right now. Start from signup or log in to request a new code.
            </div>
          ) : null}

          <div className="verification-page-actions flex flex-col gap-3 sm:flex-row">
            <button
              type="submit"
              disabled={!canVerify}
              className="verification-page-submit flex-1 rounded-full bg-[#08059B] px-6 py-3 text-lg font-bold text-white transition hover:bg-[#01044F] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {verifyOtpMutation.isPending ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 size={20} className="animate-spin" />
                  Verifying...
                </span>
              ) : (
                "Verify"
              )}
            </button>

            <button
              type="button"
              onClick={handleResend}
              disabled={!canResend}
              className="verification-page-resend flex-1 rounded-full border border-[#08059B] bg-white px-6 py-3 text-lg font-bold text-[#08059B] transition hover:bg-[#f5f7ff] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {resendOtpMutation.isPending ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 size={20} className="animate-spin" />
                  Sending...
                </span>
              ) : (
                "Resend OTP"
              )}
            </button>
          </div>
        </form>

        <div className="verification-page-note mt-5 rounded-2xl border border-[#e5e7eb] bg-white/70 px-4 py-3 text-sm text-gray-600">
          <p className="verification-page-note-title font-semibold text-gray-800">Need a new code?</p>
          <p className="verification-page-note-copy mt-1">
            If the timer expires or all {STUDENT_OTP_ATTEMPTS} attempts are used, request a new verification code.
          </p>
          <button
            type="button"
            onClick={handleResend}
            disabled={!canResend}
            className="verification-page-note-button mt-3 inline-flex items-center gap-2 text-sm font-bold text-[#08059B] underline disabled:no-underline disabled:opacity-60"
          >
            <RotateCcw size={16} />
            Request a new code
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerificationPage;
